import React from 'react';
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { Alert, AlertDescription } from "../../ui/alert"
import { useRegister } from '../hooks/Register';
import logo from '../../../assets/images/logo_no_bg.png';
import { Link } from 'react-router-dom';
import StepIndicator from "../assets/step-indicator";
import AnimatedBackground from '../../utils/AnimatedBackground';

const Register = () => {
  const {
    formData,
    error,
    showPasswords,
    handleChange,
    handleSubmit,
    setShowPasswords
  } = useRegister();

  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = 3;

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <Label className="text-white text-lg">Personal Information</Label>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="firstName" className="text-white">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-white">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="countryCode" className="text-white">Country Code</Label>
                <Input
                  id="countryCode"
                  name="countryCode"
                  type="text"
                  required
                  placeholder="+55"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="areaCode" className="text-white">Area Code</Label>
                <Input
                  id="areaCode"
                  name="areaCode"
                  type="text"
                  required
                  placeholder="11"
                  value={formData.areaCode}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="phoneNumber" className="text-white">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  required
                  placeholder="999999999"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="birthDate" className="text-white">Birth Date</Label>
              <Input
                id="birthDate"
                name="birthDate"
                type="date"
                required
                value={formData.birthDate}
                onChange={handleChange}
                className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
              />
            </div>

            <div>
              <Label htmlFor="gender" className="text-white">Gender</Label>
              <select
                id="gender"
                name="gender"
                required
                value={formData.gender}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-800 text-gray-300 shadow-sm"
              >
                <option value="">Select gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>

            <div>
              <Label htmlFor="age" className="text-white">Age</Label>
              <Input
                id="age"
                name="age"
                type="number"
                required
                min="0"
                max="120"
                value={formData.age}
                onChange={handleChange}
                className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <Label className="text-white text-lg">Address Information</Label>
            
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="street" className="text-white">Street</Label>
                <Input
                  id="street"
                  name="street"
                  type="text"
                  required
                  value={formData.street}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div className="w-32">
                <Label htmlFor="houseNumber" className="text-white">Number</Label>
                <Input
                  id="houseNumber"
                  name="houseNumber"
                  type="text"
                  required
                  value={formData.houseNumber}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="complement" className="text-white">Complement</Label>
              <Input
                id="complement"
                name="complement"
                type="text"
                value={formData.complement}
                onChange={handleChange}
                className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="city" className="text-white">City</Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="state" className="text-white">State</Label>
                <Input
                  id="state"
                  name="state"
                  type="text"
                  required
                  value={formData.state}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="country" className="text-white">Country</Label>
                <Input
                  id="country"
                  name="country"
                  type="text"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="zipCode" className="text-white">ZIP Code</Label>
                <Input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  required
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <Label className="text-white text-lg">Access Information</Label>
            
            <div>
              <Label htmlFor="email" className="text-white">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  name="password"
                  type={showPasswords.password ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, password: !showPasswords.password})}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.password ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
              <div className="mt-1 relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 placeholder:text-gray-600 text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({...showPasswords, confirmPassword: !showPasswords.confirmPassword})}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.confirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      <AnimatedBackground />
      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <img 
          src={logo}
          alt="Logo"
          className="mx-auto h-24 w-auto mb-6"
        />
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-black border border-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
          
          <form className="space-y-6" onSubmit={(e) => {
            e.preventDefault();
            if (currentStep === totalSteps) {
              handleSubmit(e);
            }
          }}>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {renderStepContent()}

            <div className="flex justify-between gap-4">
              {currentStep > 1 && (
                <Button 
                  type="button" 
                  onClick={prevStep}
                  className="flex-1 bg-gray-600 hover:bg-gray-500"
                >
                  Previous
                </Button>
              )}
              <Button 
                type="button"
                onClick={currentStep === totalSteps ? handleSubmit : nextStep}
                className="flex-1 bg-blue-600 hover:bg-blue-500"
              >
                {currentStep === totalSteps ? "Register" : "Next"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <div className="mt-6 text-center relative z-10">
        <Link to="/login" className="font-medium text-blue-500 hover:text-blue-400">
          Already have an account? Sign in
        </Link>
      </div>
    </div>
  );
};

export default Register;